import {gql} from '@apollo/client';
import type {NormalizedCacheObject, ApolloClient} from '@apollo/client';
import debounce from 'lodash.debounce';

import type {
  AckMutation,
  AckMutationVariables,
  StaffAccount,
} from '../types/graphql';
import type {Message} from '../cache/message';
import {unreadMessages} from './AllClients.gql';
import * as log from '../logger';

/**
 * Acknowledge events on the server.
 */
export const ACK = gql`
  mutation Ack($ids: [ID!]!) {
    markSeenEvents(ids: $ids) {
      unread
    }
  }
`;

/**
 * Queue of (ClientId, MsgId) to acknowledge.
 */
const ackQueue: [string, string][] = [];

/**
 * Acknowledge a batch of messages.
 */
const ackBatch = async (apolloClient: ApolloClient<NormalizedCacheObject>) => {
  const unreads = unreadMessages();

  const ackedEventIds: string[] = [];

  while (ackQueue.length) {
    const [clientId, msgId] = ackQueue.pop()!;
    const clientUnreads = unreads.get(clientId);
    if (!clientUnreads) {
      // If the client has no unreads, just skip it.
      continue;
    }
    // Keep track of the messages that were actually newly acked.
    if (clientUnreads.has(msgId)) {
      ackedEventIds.push(clientUnreads.get(msgId)!);
      clientUnreads.delete(msgId);
    }
  }

  unreadMessages(new Map(unreads));

  if (!ackedEventIds.length) {
    return;
  }

  // Ack on the server side
  return apolloClient.mutate<AckMutation, AckMutationVariables>({
    mutation: ACK,
    variables: {
      ids: ackedEventIds,
    },
  });
};

/**
 * Debounced call to ackBatch.
 */
const debouncedAckBatch = debounce(ackBatch, 50);

/**
 * Mark message with given ID in given client ID thread as read.
 */
const ack = (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  clientId: string,
  msgId: string,
) => {
  ackQueue.push([clientId, msgId]);
  debouncedAckBatch(apolloClient);
};

/**
 * Check if an account is a staff account.
 */
const isStaffAccount = (account: unknown): account is StaffAccount => {
  if (!account) {
    return false;
  }

  if (!Object.hasOwn(account, '__typename')) {
    return false;
  }

  return (account as {__typename: string}).__typename === 'StaffAccount';
};

/**
 * Mark a message as read.
 */
export const ackMessage = (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  msg: Message,
) => {
  const account = msg.source?.account;
  if (!account || !Object.hasOwn(account, '__typename')) {
    return;
  }
  const clientId = isStaffAccount(account)
    ? msg.destination?.account?.id
    : msg.source?.account?.id;

  if (!clientId) {
    log.error(`Missing client ID from message ${msg.id}! can't ack it.`);
    return;
  }

  ack(apolloClient, clientId, msg.id);
};
